/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { deleteCostDb, saveAddEditCostDb } from "@Actions/portalSetting";

import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { convertDate } from "@Services/luxonHelper";
import { GrClose } from "react-icons/gr";
import { showToastNotification } from "@Reducers/toastSlice";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import Forbidden from "@Pages/Error/Forbidden";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import CostDatabaseProperties from "./CostDatabaseProperties";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { getCostDatabaseDataUrl, getCurrencyDataUrl } from "@Services/dataManager";
import axiosWrapper from "@AxiosWrapper";
import type { CurrencyDataItem, GetCurrencyDataResponse } from "@ServerTypes/currency/currency.schema";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SlickGrid1, { withDynamicColumns } from "@CubeComponents/html/SlickGrid1";
import IconComponent from "@CubeComponents/icons";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import _ from "lodash";
import Text from "@CubeComponents/html/Text/Text";
import { Box } from "@mui/material";
import type { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import { GridPageLayout } from "@CubeComponents/html/GridPageLayout";
import { RootState } from "@Reducers/index";
import type { ReactElement, SyntheticEvent } from "react";
import type { GridClickEvent, GridRowBase, QueryShape, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { CostDatabaseData, SaveCostDatabaseRequest } from "@ServerTypes/costDatabase/costDatabase.schema";
import { toObjectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { CostDatabaseDetails } from "./CostDatabaseProperties";

/**
 * One row of the Cost Databases list grid. `costDatabaseDataSchema` / `CostDatabaseData`
 * (costDatabase.schema) is the server row shape; `_id`, `currency.id` and `createdBy._id` are
 * declared `z.unknown()` there, so they are re-narrowed here to the 24-hex ObjectId every row
 * actually carries — that is what this grid navigates with and posts back.
 */
type CostDatabaseRow = GridRowBase &
  Omit<CostDatabaseData, "_id" | "currency" | "createdBy"> & {
    _id?: ObjectIdString;
    currency?: { id?: ObjectIdString; name?: string };
    createdBy?: { _id?: ObjectIdString; fullName?: string; profilePic?: string };
  };

interface CostDatabasesProps {
  /** Renders the bare grid (no page header, no toolbar, no row-click navigation, no properties panel). */
  showOnlyGrid?: boolean;
  /**
   * Extra grid query, forwarded verbatim to SlickGrid1's `query` prop.
   * RateAnalysis / WBSItemDetails use it to restrict the list to published versions and a project.
   */
  costDatabaseQuery?: QueryShape | Record<string, unknown>;
  /** Enables/disables the caller's own action button from the selection count. */
  setBtnState?: (enabled: boolean) => void;
  /** The row the caller wants pre-selected once the grid is ready; only its `_id` is read. */
  selectedCDB?: { _id?: ObjectIdString };
  /**
   * Selection callbacks. Typed permissively because the two external callers supply handlers with
   * their own row types (`(rows: CostDatabaseRecord[]) => void` in RateAnalysis, a
   * `Dispatch<SetStateAction<SelectedCDB>>` in WBSItemDetails); the call sites below cast to the
   * shape this file actually passes, exactly as `Groups`/`UsersInGroup` do for `setGroupData`.
   */
  costDatabaseSelection?: (...args: never[]) => void;
  setSelectedCDB?: (...args: never[]) => void;
  /**
   * KNOWN BUG, preserved verbatim: WBSItemDetails passes `allowSingleSelection` but this component
   * never reads it — the flag is inert, so that picker still allows multi-select. Declared here only
   * so the existing caller keeps compiling. Fix: honour it (or drop it from the caller).
   */
  allowSingleSelection?: boolean;
}

/**
 * The create/edit Cost Database modal's fields, derived from the server write schema
 * (`saveCostDatabaseRequest` in costDatabase.schema). `currency` holds what is sent as
 * `currencyId`; `defaultCostIndex` additionally allows the `null` the number input resets to
 * (the save is blocked while it is null, since `null <= 0`); `action` is the local flag that
 * decides which modal is open.
 */
type CostDatabaseFormState = {
  action: "" | "create" | "edit";
  name: SaveCostDatabaseRequest["name"];
  description: SaveCostDatabaseRequest["description"];
  defaultCostIndex: SaveCostDatabaseRequest["defaultCostIndex"] | null;
  currency?: SaveCostDatabaseRequest["currencyId"];
  _id?: SaveCostDatabaseRequest["_id"];
};

/** The grid's dataView, as this screen walks it for prev/next navigation and selection reads. */
type CostDatabaseDataView = SlickGrid1Ref<CostDatabaseRow>["dataView"];

/**
 * `getAllSelectedItems()` is declared `T[]` by @slickgrid-universal/common
 * (dist/types/core/slickDataview.d.ts) but its implementation pushes `getItemById(id)` for every
 * selected id, which is `undefined` once a remote / infinite-scroll refresh drops that row —
 * SlickGrid1 filters the same nullish holes out itself, but only when `enableSearchBar` is set,
 * which this grid does not set. So the rows are re-typed to what the DataView can really return
 * and the holes (plus the placeholder rows) are dropped here.
 */
const selectedRowsOf = (dataView: CostDatabaseDataView): CostDatabaseRow[] =>
  (dataView.getAllSelectedItems() as (CostDatabaseRow | undefined)[]).filter(
    (record): record is CostDatabaseRow => record != null && !record.__isDummy,
  );

/**
 * The failure `errorMessage` as the browser can actually receive it. `saveCostDatabaseResponse`
 * (statusResponse) declares it required on the `status: false` branch, but the axios interceptor
 * synthesises that same envelope itself: its 429 branch fills `errorMessage` from
 * `error?.response?.data?.errorMessage` (services/axiosShared.ts), which can be absent. Reading it
 * through this widening keeps the `?? SOMETHING_WENT_WRONG` fallbacks below live rather than dead.
 */
const errorMessageOf = (data: { errorMessage?: string }): string | undefined => data.errorMessage;

/** One toolbar entry of this grid; `name` is the label SlickGrid1's ToolBar actually renders. */
type CostDatabasesToolbarItem = {
  id: string;
  name?: string;
  prefixIcon?: string;
  rightAllign?: boolean;
  template?: () => ReactElement;
  disabled: boolean;
};

function CostDatabases(props: CostDatabasesProps) {
  const { t } = useTranslation();
  const { showOnlyGrid, costDatabaseSelection, costDatabaseQuery } = props;

  const buttonInitialState = {
    create: false,
    edit: true,
    delete: true,
    properties: true,
  };

  const initialNewCostDatabaseData: CostDatabaseFormState = {
    action: "",
    name: "",
    description: "",
    defaultCostIndex: 1,
    // Nothing picked yet. `validationForCreateAndEditCostDatabase` blocks the create path until the
    // AutoComplete supplies a real currency id, so an unset value can never reach the request.
    currency: undefined,
  };

  const [costDatabaseDetails, setCostDatabaseDetails] = useState<CostDatabaseDetails>({});
  const [selectedRowIndexCostDatabase, setSelectedRowIndexCostDatabase] = useState(0);
  const [costDatabaseData, setCostDatabaseData] = useState<CostDatabaseFormState>(initialNewCostDatabaseData);
  const [gridQuery] = useState(costDatabaseQuery ?? {});
  const [toolbarButtonDisableState, setToolbarButtonDisableState] = useState(buttonInitialState);
  const [isGridReady, setIsGridReady] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyDataItem[]>([]);

  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dataGrid = useRef<SlickGrid1Ref<CostDatabaseRow> | null>(null);

  useEffect(() => {
    document.title = t("Portal Settings | Cost Database");
  }, []);

  useEffect(() => {
    const fetchCurrencyData = async () => {
      try {
        const response = await axiosWrapper.post<GetCurrencyDataResponse>(getCurrencyDataUrl, {
          value: { params: {} },
        });
        if (response.data.result) {
          setCurrencyOptions(response.data.result);
        }
      } catch (error) {
        console.error("Error fetching currency data:", error);
        Promise.reject(error);
      }
    };
    fetchCurrencyData();
  }, []);

  const propertiesBtnTemplate = () => (
    <div className="me-7" data-testid="propertiesCostDatabaseBtn">
      <IconComponent
        name="properties"
        width="1.25rem"
        height="1.25rem"
        onClick={() => toolbarClickHandler("properties")}
      />
    </div>
  );

  const toolbarOptions: CostDatabasesToolbarItem[] = [
    {
      id: "create",
      name: t("Add Cost Database"),
      prefixIcon: "add",
      disabled: toolbarButtonDisableState.create,
    },
    {
      id: "edit",
      name: t("Edit"),
      prefixIcon: "editProfile",
      disabled: toolbarButtonDisableState.edit,
    },
    {
      id: "delete",
      name: t("Delete"),
      prefixIcon: "delete",
      disabled: toolbarButtonDisableState.delete,
    },
    {
      id: "properties",
      rightAllign: true,
      template: propertiesBtnTemplate,
      disabled: toolbarButtonDisableState.properties,
    },
  ];

  useEffect(() => {
    setToolbarButtonDisableState((prev) => ({
      ...prev,
      create: !portalSettingInfo.costDatabase?.addCostDatabases?.enable,
    }));
  }, [portalSettingInfo.costDatabase]);

  const formatDate = (data: number, value: number, a: unknown, b: unknown, props: CostDatabaseRow) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    const dateFormat = convertDate(Number(props.createdDate), "dateForTooltip");
    root.render(
      <div style={{ width: "fit-content" }}>
        <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
          <span>{convertDate(Number(props.createdDate), "dateForText")}</span>
        </ToolTipComponent>
      </div>,
    );

    return container;
  };

  const recordClick = (args: GridClickEvent) => {
    if (args.detail.args.cell !== 0) {
      const rowData: CostDatabaseRow = dataGrid.current!.slickGrid.getDataItem(args.detail.args.row);
      if (rowData._id) {
        setCostDatabaseDetails({});
        navigate(`/portalSettings/costDatabases/${rowData._id}`);
      }
    }
  };

  const selectionEvent = () => {
    if (!_.get(dataGrid, "current")) return;

    const selectedRecords = selectedRowsOf(dataGrid.current!.dataView);

    if (showOnlyGrid && costDatabaseSelection) {
      // RateAnalysis declares this handler over its own `CostDatabaseRecord` (`_id`/`name` REQUIRED),
      // which no `CostDatabaseRow` type can be contravariantly assigned to — the row's fields come
      // from the server schema, where both are optional. Typing the prop for the rows this file
      // actually passes therefore has to happen in RateAnalysis first.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see note above
      (costDatabaseSelection as (rows: CostDatabaseRow[]) => void)(selectedRecords);
      return;
    }

    if (props.setBtnState) {
      props.setBtnState(selectedRecords.length === 1);
      if (selectedRecords.length === 1 && props.setSelectedCDB) {
        // WBSItemDetails passes a `Dispatch<SetStateAction<SelectedCDB>>`, which accepts neither
        // `undefined` nor this file's `currency` shape; same blocker as `costDatabaseSelection`.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see note above
        (props.setSelectedCDB as (row: CostDatabaseRow | undefined) => void)(selectedRecords[0]);
      }
    }
    if (props.showOnlyGrid) {
      return;
    }

    const canEdit = portalSettingInfo.costDatabase?.editCostDatabases?.enable;
    const canDelete = portalSettingInfo.costDatabase?.deleteCostDatabases?.enable;

    setToolbarButtonDisableState((prev) => ({
      ...prev,
      edit: selectedRecords.length !== 1 || !canEdit,
      delete:
        selectedRecords.length == 0 || selectedRecords.some((record) => !record.createdBy?.fullName) || !canDelete,
      properties: selectedRecords.length !== 1,
    }));
    if (selectedRecords.length !== 1) {
      setCostDatabaseDetails({});
      setSelectedRowIndexCostDatabase(0);
    }
  };

  const toolbarClickHandler = async (toolClicked: string) => {
    const data = selectedRowsOf(dataGrid.current!.dataView);

    switch (toolClicked) {
      case "create":
        setCostDatabaseData((prev) => ({ ...prev, action: "create" }));
        break;
      case "edit":
        // KNOWN BUG, preserved verbatim: "edit" is only enabled for exactly one selected row, but
        // nothing re-checks that here — `data[0]` and its name/description are dereferenced
        // unconditionally, so a stale/empty selection throws. The `!`s keep that crash.
        setCostDatabaseData({
          action: "edit",
          name: data[0]!.name!,
          description: data[0]!.description!,
          defaultCostIndex: data[0]!.defaultCostIndex!,
          _id: data[0]!._id,
        });
        break;
      case "delete":
        setOpenDeleteModal(true);
        break;
      case "properties":
        openProperties({ index: null, isForward: null });
        break;
    }
  };

  const createdByTemplate = (data: number, value: number, a: unknown, b: unknown, args: CostDatabaseRow) => {
    const container = document.createElement("div");

    if (args.createdBy && typeof args.createdBy !== "string") {
      renderUserTag(container, { id: args.createdBy._id });
    } else {
      container.textContent = t("SYSTEM GENERATED");
    }
    return container;
  };

  const handleCurrencyChange = (_event: SyntheticEvent, newValue: CurrencyDataItem | null) => {
    // `CurrencyDataItem.id` is the currency subdocument's 24-hex `_id` as the server projected it,
    // so it is already a valid ObjectId; `toObjectIdString` tags it and propagates a cleared field.
    setCostDatabaseData((prev) => ({ ...prev, currency: toObjectIdString(newValue?.id) }));
  };

  const validationForCreateAndEditCostDatabase = (showToast = false) => {
    // `defaultCostIndex` is null while the number input is empty. That used to be rejected through
    // `null <= 0` being true in JS; the null check below is that same rejection, spelled out.
    if (
      costDatabaseData.defaultCostIndex === null ||
      Number.isNaN(costDatabaseData.defaultCostIndex) ||
      costDatabaseData.defaultCostIndex <= 0
    ) {
      if (showToast) {
        dispatch(
          showToastNotification({
            content: t("Please enter a positive value for Default Cost Index."),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      return false;
    }
    if (
      !costDatabaseData.name.trim() ||
      !costDatabaseData.description.trim() ||
      (!costDatabaseData._id && !costDatabaseData.currency)
    ) {
      return false;
    }
    return true;
  };

  const createCostDatabase = async () => {
    const data = {
      name: costDatabaseData.name.trim(),
      description: costDatabaseData.description.trim(),
      currencyId: costDatabaseData.currency,
      // Non-null because the submit button is `disabled={!validationForCreateAndEditCostDatabase()}`
      // and that check rejects the empty input: `defaultCostIndex` is `null` while the number field is
      // blank, and `null <= 0` is `true` in JS (see the note at its guard). The schema requires a
      // number, so `null` could never legally be sent. Erased at emit.
      defaultCostIndex: costDatabaseData.defaultCostIndex!,
    };
    const response = await saveAddEditCostDb(data);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("create_costDatabase", { name: costDatabaseData.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      closeCDBModal();
      dataGrid.current?.refresh!();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(errorMessageOf(response.data) ?? SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const editCostDatabase = async () => {
    const data = {
      name: costDatabaseData.name.trim(),
      description: costDatabaseData.description.trim(),
      _id: costDatabaseData._id,
      // Same guard as the create path: the submit button is disabled unless
      // `validationForCreateAndEditCostDatabase()` passes, and that rejects the blank number input
      // (`null <= 0` is `true`). Erased at emit.
      defaultCostIndex: costDatabaseData.defaultCostIndex!,
    };

    const response = await saveAddEditCostDb(data);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("edit_costDatabase", { name: data.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      closeCDBModal();
      dataGrid.current?.refresh!();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(errorMessageOf(response.data) ?? SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const createEditCD = async () => {
    setIsButtonLoading(true);
    const validationResults = validationForCreateAndEditCostDatabase(true);
    if (validationResults) {
      if (costDatabaseData._id) {
        await editCostDatabase();
      } else {
        await createCostDatabase();
      }
    }
    setIsButtonLoading(false);
  };

  const closeCDBModal = () => {
    setCostDatabaseData(initialNewCostDatabaseData);
  };

  const createCostDbModal = () => (
    <div>
      <Box className="fixed inset-0 z-[1000] bg-[var(--background-scrim)]" onClick={() => closeCDBModal()} />
      <div className="fixed start-1/2 top-1/2 z-[1000] w-[25rem] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-8">
        <div className="flex w-full items-center justify-between pb-4">
          <Text
            className="text-[1.375rem] font-semibold"
            data-testid={costDatabaseData._id ? "editCostDatabase" : "createNewCostDatabase"}
          >
            {" "}
            {costDatabaseData._id ? t("Edit Cost Database") : t("Create New Cost Database")}
          </Text>
          <GrClose size={20} className="cursor-pointer" onClick={() => closeCDBModal()} data-testid="closeCDModalBtn" />
        </div>
        <div className="flex flex-col">
          <label htmlFor="Name" className="mb-2 mt-4 text-sm text-[#333]" data-testid="createCDNameLabel">
            <Text>{t("Cost Database")}</Text>
          </label>
          <input
            data-testid="createCDNameInputBox"
            className="mb-[0.4rem] w-full resize-none border border-[#19204880] px-2 py-[0.42rem] outline-none placeholder:text-[#19204880] focus:border-[#0078d4] focus:py-[0.44rem]"
            type="text"
            minLength={1}
            maxLength={255}
            name="Name"
            placeholder={t("Cost Database")}
            value={costDatabaseData.name}
            onChange={(event) =>
              setCostDatabaseData((prev: CostDatabaseFormState) => ({ ...prev, name: event.target.value }))
            }
          />
          <label htmlFor="description" className="mb-2 mt-4 text-sm text-[#333]" data-testid="createCDDescriptionLabel">
            <Text>{t("Description")}</Text>
          </label>
          <textarea
            className="mb-[0.4rem] w-full resize-none border border-[#19204880] px-2 py-[0.42rem] outline-none placeholder:text-[#19204880] focus:border-[#0078d4] focus:py-[0.44rem]"
            // type="textarea"
            rows={5}
            minLength={1}
            maxLength={255}
            name="description"
            placeholder={t("Cost database description")}
            value={costDatabaseData.description}
            onChange={(event) =>
              setCostDatabaseData((prev: CostDatabaseFormState) => ({ ...prev, description: event.target.value }))
            }
            data-testid="createCDDescriptionInputBox"
          />
          <label className="mb-2 mt-4 text-sm text-[#333]" data-testid="createCDDefaultCDIndexLabel">
            {" "}
            <Text>{t("Default Cost Index")} </Text>
          </label>
          <TextFieldComponent
            type="number"
            placeholder={t("Enter Value")}
            value={costDatabaseData.defaultCostIndex}
            onChange={(event) =>
              setCostDatabaseData((prev: CostDatabaseFormState) => ({
                ...prev,
                defaultCostIndex: event.target.value ? Number(event.target.value) : null,
              }))
            }
            slotProps={{
              htmlInput: {
                "data-testid": "createCDDefaultCDIndexInputBox",
              },
            }}
          />
          {costDatabaseData._id ? null : (
            <>
              <label htmlFor="Currency" className="mb-2 mt-4 text-sm text-[#333]" data-testid="currencyLabel">
                <Text>{t("Currency")}</Text>
              </label>
              <AutoComplete
                valueField="id"
                getOptionLabel={(option) => `${option.name} - ${option.selected_currency_symbol?.selectedSymbol || ""}`}
                onChange={handleCurrencyChange}
                options={currencyOptions}
                data-testid="createCDCurrencyDropdown"
                // Guarded: while no currency is picked both sides are `undefined`, and an unguarded
                // `find` would then match the first option that has no `id` of its own.
                value={
                  costDatabaseData.currency
                    ? (currencyOptions.find((opt) => opt.id === costDatabaseData.currency) ?? null)
                    : null
                }
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextFieldComponent {...params} placeholder={t("Select Currency")} />
                )}
              />
            </>
          )}
        </div>
        <div className="mt-8 flex justify-between">
          <ButtonComponent
            data-testid={costDatabaseData._id ? "saveCostDatabase" : "createCostDatabase"}
            disabled={!validationForCreateAndEditCostDatabase()}
            onClick={createEditCD}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            loading={isButtonLoading}
          >
            <Text>{t(costDatabaseData._id ? "Save" : "Create")}</Text>
          </ButtonComponent>
        </div>
      </div>
    </div>
  );

  const dataBound = (val: boolean) => {
    setIsGridReady(val);
  };

  // `withDynamicColumns` rather than `typedColumns`: the Currency column addresses a nested value by
  // path ("currency.name"), which can never be a `keyof CostDatabaseRow`.
  const [gridColumns] = useState(() =>
    withDynamicColumns<CostDatabaseRow>([
      {
        field: "name",
        name: t("Name"),
        id: "name",
        textAlign: "Left",
        filterType: "Text",
        minWidth: 170,
      },
      {
        field: "description",
        name: t("Description"),
        id: "description",
        textAlign: "Left",
        filterType: "Text",
        minWidth: 170,
      },
      {
        field: "defaultCostIndex",
        name: t("Default Cost Index"),
        id: "defaultCostIndex",
        textAlign: "Left",
        filterType: "Number",
        minWidth: 120,
      },
      {
        field: "currency.name",
        name: t("Currency"),
        id: "currencyName",
        textAlign: "Left",
        filterType: "Text",
        clipMode: "EllipsisWithTooltip",
        _valueGetter: (props: CostDatabaseRow) => props.currency!.name,
        minWidth: 150,
      },
      {
        field: "createdDate",
        name: t("Created On"),
        id: "createdDate",
        textAlign: "Left",
        // filterType: "Date",
        formatter: formatDate,
        minWidth: 120,
      },
      {
        field: "createdBy",
        name: t("Created by"),
        id: "createdBy",
        textAlign: "Left",
        filterType: "Text",
        formatter: createdByTemplate,
        _valueGetter: (props: CostDatabaseRow) => props.createdBy!.fullName,
        minWidth: 150,
      },
    ]),
  );

  const CostDatabaseGridData = () => (
    <div className="overflow-auto h-full w-full">
      <SlickGrid1
        ref={dataGrid}
        columnDefinitions={gridColumns}
        enableSortingOption
        onSelectedRowIdsChanged={selectionEvent}
        remoteDataSource={getCostDatabaseDataUrl}
        query={gridQuery}
        infiniteScroll
        enableRowResize
        enableFilteringOption
        enableColumnChooser
        setGridReady={dataBound}
        toolbar={showOnlyGrid ? undefined : toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        onClick={showOnlyGrid ? undefined : recordClick}
      />
    </div>
  );

  function openProperties({ index, isForward }: { index: number | null; isForward: boolean | null }) {
    let itemData;
    if (index != null) {
      const condition = isForward ? 1 : -1;
      // `getItems()` is the DataView's public accessor for the row array this walks; `items` itself
      // is declared `protected` by @slickgrid-universal/common.
      const items = dataGrid.current!.dataView.getItems();
      if (items.length == index + condition) {
        itemData = items[0];
        setSelectedRowIndexCostDatabase(0);
        dataGrid.current!.slickGrid.setSelectedRows([0]);
      } else if (index + condition == -1) {
        itemData = items[items.length - 1];
        setSelectedRowIndexCostDatabase(items.length - 1);
        dataGrid.current!.slickGrid.setSelectedRows([items.length - 1]);
      } else {
        itemData = items[index + condition];
        setSelectedRowIndexCostDatabase(index + condition);
        dataGrid.current!.slickGrid.setSelectedRows([index + condition]);
      }
    } else {
      // `getSelectedRows()[0]` is `number | undefined` only because of `noUncheckedIndexedAccess`.
      // This branch is the `index == null` path of `openProperties`, reached from the Properties
      // toolbar button, which is enabled only while a row is selected — so the array is non-empty.
      // Erased at emit; an empty selection would pass `undefined` through exactly as it does today.
      const index = dataGrid.current!.slickGrid.getSelectedRows()[0]!;
      setSelectedRowIndexCostDatabase(index);
      itemData = dataGrid.current!.slickGrid.getDataItem(index);
    }
    if (itemData) {
      const { _id, name, description, defaultCostIndex, currency, createdDate, createdBy } = itemData;
      setCostDatabaseDetails({
        _id,
        name,
        description,
        defaultCostIndex,
        currencyId: currency.id,
        createdDate,
        createdBy: createdBy?.fullName ?? "",
        createdById: createdBy?._id,
        currencyName: currency.name,
      });
    }
  }

  useEffect(() => {
    if (isGridReady && props.selectedCDB) {
      // `props.selectedCDB` is narrowed non-undefined by the enclosing `if`, but the narrowing does
      // not survive into this callback (it is a mutable prop read). Erased at emit.
      const index = dataGrid.current!.dataView.getItems().findIndex((record) => record._id == props.selectedCDB!._id);
      if (index != -1) {
        dataGrid.current!.slickGrid.setSelectedRows([index]);
      }
    }
  }, [isGridReady]);

  const handleCloseSidebar = () => {
    setCostDatabaseDetails({});
  };

  const cancelDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleDelete = async () => {
    setIsButtonLoading(true);
    const selectedRecords = selectedRowsOf(dataGrid.current!.dataView);
    const response = await deleteCostDb(selectedRecords.map((record: CostDatabaseRow) => record._id!));
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("Cost Database(s) have been deleted successfully."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      dataGrid.current?.refresh!();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setIsButtonLoading(false);
    cancelDeleteModal();
  };

  const deleteFooter = () => (
    <div className="w-full flex gap-5">
      <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleDelete} loading={isButtonLoading}>
        <Text>{t("Delete")}</Text>
      </ButtonComponent>
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        onClick={cancelDeleteModal}
        disabled={isButtonLoading}
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const deleteCostDatabaseModal = () => (
    <ModalStateDialogComponent
      footerContent={deleteFooter()}
      headerTitle={t("Delete Cost Database(s)")}
      isVisible={openDeleteModal}
      onCancel={isButtonLoading ? undefined : cancelDeleteModal}
      modelContent={
        <Text className="text-sm" sx={{ whiteSpace: "normal" }}>
          {t("Are you sure you want to delete the selected Cost Database(s)?")}
        </Text>
      }
      width="28.125rem"
    />
  );

  return (
    <>
      {!portalSettingInfo.costDatabase?.isVisible && !showOnlyGrid ? (
        <Forbidden />
      ) : (
        <GridPageLayout
          showHeader={!showOnlyGrid}
          title="Cost Databases"
          description="List of all the cost databases in this portal"
        >
          <div className="flex justify-between gap-3 h-full w-full overflow-hidden">
            {CostDatabaseGridData()}
            {costDatabaseDetails._id && (
              <CostDatabaseProperties
                costDatabaseDetails={costDatabaseDetails}
                openProperties={openProperties}
                index={selectedRowIndexCostDatabase}
                setShowProperties={handleCloseSidebar}
              />
            )}
          </div>
        </GridPageLayout>
      )}
      {costDatabaseData.action && createCostDbModal()}
      {openDeleteModal && deleteCostDatabaseModal()}
    </>
  );
}

export default CostDatabases;
